import React, { useState, useEffect, useMemo } from 'react';
import { Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Typography, Button, TextField, Grid, MenuItem } from '@mui/material';
import { useQueryCoordinate } from '../../../clients/graphql/graphql.hooks';
import { gql } from '@apollo/client';
import { Link, useSearchParams } from 'react-router-dom';

const EXECUTIONS_FOR_WORKFLOW_GET = gql`
  query ExecutionsForWorkflowGet($input: ExecutionsForWorkflowInput!) {
    executionsForWorkflowGet(input: $input) {
      spans {
        executionId
        attempt
        originalStepId
        stepName
        scheduledFor
        startedAt
        endAt
        inputPayload
        outputResults
        __typename
      }
      nextPageToken
      pageSize
      __typename
    }
  }
`;

const LIST_WORKFLOW_EXECUTIONS = gql`
  query ListWorkflowExecutions($input: WorkflowExecutionsInput!) {
    listWorkflowExecutions(input: $input) {
      spans {
        executionId
        attempt
        originalStepId
        stepName
        scheduledFor
        startedAt
        endAt
        inputPayload
        outputResults
        __typename
      }
      nextPageToken
      pageSize
      __typename
    }
  }
`;

type QueryType = 'AutoPilotSettings' | 'PortfolioKey' | 'UserKey' | 'WorkflowName' | 'WorkflowID';

const QUERY_TYPE_OPTIONS: { value: QueryType; label: string }[] = [
  { value: 'WorkflowName', label: 'Workflow Name' },
  { value: 'WorkflowID', label: 'Workflow ID' },
  { value: 'UserKey', label: 'User Key' },
  { value: 'PortfolioKey', label: 'Portfolio Key' },
  { value: 'AutoPilotSettings', label: 'Autopilot Settings Key' },
];

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100, 200];

interface WorkflowSpansTabProps {
  workflowId: string;
  spanType: 'WORKFLOW' | 'CRON';
}

const formatDate = (date: Date | null): string => {
  if (!date) return '-';
  return date.toLocaleString();
};

const formatDuration = (startedAt: Date | null, endAt: Date | null): string => {
  if (!startedAt || !endAt) return '-';
  const duration = endAt.getTime() - startedAt.getTime();
  const seconds = Math.floor(duration / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);

  if (hours > 0) {
    return `${hours}h ${minutes % 60}m ${seconds % 60}s`;
  } else if (minutes > 0) {
    return `${minutes}m ${seconds % 60}s`;
  } else {
    return `${seconds}s`;
  }
};

export const WorkflowSpansTab: React.FC<WorkflowSpansTabProps> = ({ workflowId, spanType }) => {
  const [searchParams, setSearchParams] = useSearchParams();

  // Applied filter state (from URL) – these are what actually trigger queries
  const getQueryTypeFromUrl = (): QueryType | null => {
    const param = searchParams.get('queryType') as QueryType | null;
    return param && QUERY_TYPE_OPTIONS.some((opt) => opt.value === param)
      ? param
      : null;
  };

  const getSearchValueFromUrl = () => {
    return searchParams.get('searchValue') || '';
  };

  const getPageSizeFromUrl = (): number => {
    const param = searchParams.get('pageSize');
    if (!param) return 10;
    const n = parseInt(param, 10);
    return PAGE_SIZE_OPTIONS.includes(n) ? n : 10;
  };

  // Applied filters (used in queries)
  const [appliedQueryType, setAppliedQueryType] = useState<QueryType | null>(getQueryTypeFromUrl);
  const [appliedSearchValue, setAppliedSearchValue] = useState<string>(getSearchValueFromUrl);

  // Draft filter state (what user is typing, not yet applied)
  const [queryType, setQueryType] = useState<QueryType | null>(getQueryTypeFromUrl);
  const [searchValue, setSearchValue] = useState<string>(getSearchValueFromUrl);

  // Determine if filters are active (based on applied values)
  const hasActiveFilters = appliedQueryType !== null && appliedSearchValue.trim() !== '';

  // Cursor-based pagination state
  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  const [pageTokens, setPageTokens] = useState<(string | null)[]>([null]);
  const [pageSize, setPageSize] = useState<number>(getPageSizeFromUrl());

  // Load pagination and filters from URL on component mount
  useEffect(() => {
    const urlPage = searchParams.get('page');
    const pageIndex = urlPage ? Math.max(0, parseInt(urlPage, 10)) : 0;
    setCurrentPageIndex(pageIndex);
    setPageSize(getPageSizeFromUrl());

    // Sync applied filter state from URL (these trigger queries)
    const urlQueryType = getQueryTypeFromUrl();
    const urlSearchValue = getSearchValueFromUrl();
    setAppliedQueryType(urlQueryType);
    setAppliedSearchValue(urlSearchValue);

    // Also sync draft values to match (so form shows current state)
    setQueryType(urlQueryType);
    setSearchValue(urlSearchValue);
  }, [searchParams]);

  // Update URL with pagination and filters
  const updateUrlParams = (updates: {
    page?: number;
    pageSize?: number;
    queryType?: QueryType | null;
    searchValue?: string;
  }) => {
    const newSearchParams = new URLSearchParams(searchParams);

    // Update page
    if (updates.page !== undefined) {
      if (updates.page > 0) {
        newSearchParams.set('page', updates.page.toString());
      } else {
        newSearchParams.delete('page');
      }
    }

    if (updates.pageSize !== undefined) {
      if (updates.pageSize !== 10) {
        newSearchParams.set('pageSize', updates.pageSize.toString());
      } else {
        newSearchParams.delete('pageSize');
      }
    }

    if (updates.queryType !== undefined) {
      if (updates.queryType) {
        newSearchParams.set('queryType', updates.queryType);
      } else {
        newSearchParams.delete('queryType');
      }
    }

    if (updates.searchValue !== undefined) {
      if (updates.searchValue) {
        newSearchParams.set('searchValue', updates.searchValue);
      } else {
        newSearchParams.delete('searchValue');
      }
    }

    setSearchParams(newSearchParams);
  };

  const currentPageToken = pageTokens[currentPageIndex] ?? null;

  // Use executionsForWorkflowGet by default, listWorkflowExecutions when filters are active
  const useFilteredQuery = hasActiveFilters && appliedQueryType !== null && appliedSearchValue.trim() !== '';

  const { loading, error, data } = useQueryCoordinate(
    useFilteredQuery ? LIST_WORKFLOW_EXECUTIONS : EXECUTIONS_FOR_WORKFLOW_GET,
    {
      variables: useFilteredQuery
        ? {
            input: {
              workflowName: workflowId,
              queryType: appliedQueryType!,
              searchValue: appliedSearchValue.trim(),
              pageSize,
              pageToken: currentPageToken,
            },
          }
        : {
            input: {
              workflowName: workflowId,
              spanType: spanType,
              pageSize,
              pageToken: currentPageToken,
            },
          },
    }
  );

  const spans = useMemo(() => {
    if (useFilteredQuery) {
      return data?.listWorkflowExecutions?.spans || [];
    }
    return data?.executionsForWorkflowGet?.spans || [];
  }, [data, useFilteredQuery]);

  const nextPageToken = useFilteredQuery
    ? data?.listWorkflowExecutions?.nextPageToken || null
    : data?.executionsForWorkflowGet?.nextPageToken || null;

  const handleApplyFilter = () => {
    // Apply the draft values to the applied values (this triggers the query)
    setAppliedQueryType(queryType);
    setAppliedSearchValue(searchValue);

    // Reset pagination when filter changes
    setCurrentPageIndex(0);
    setPageTokens([null]);
    updateUrlParams({ page: 0, queryType, searchValue });
  };

  const handleClearFilter = () => {
    // Clear both draft and applied values
    setQueryType(null);
    setSearchValue('');
    setAppliedQueryType(null);
    setAppliedSearchValue('');

    setCurrentPageIndex(0);
    setPageTokens([null]);
    updateUrlParams({ page: 0, queryType: null, searchValue: '' });
  };

  const handleNextPage = () => {
    if (!nextPageToken) return;
    const newIndex = currentPageIndex + 1;
    const newTokens = [...pageTokens];
    newTokens[newIndex] = nextPageToken;
    setPageTokens(newTokens);
    setCurrentPageIndex(newIndex);
    updateUrlParams({ page: newIndex });
  };

  const handlePreviousPage = () => {
    if (currentPageIndex === 0) return;
    const newIndex = currentPageIndex - 1;
    setCurrentPageIndex(newIndex);
    updateUrlParams({ page: newIndex });
  };

  const handlePageSizeChange = (newSize: number) => {
    setPageSize(newSize);
    setCurrentPageIndex(0);
    setPageTokens([null]);
    updateUrlParams({ page: 0, pageSize: newSize });
  };

  if (loading) return <Typography>Loading...</Typography>;
  if (error) return <Typography color="error">Error: {error.message}</Typography>;

  return (
    <Box>
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4} md={3}>
            <TextField
              select
              fullWidth
              size="small"
              label="Filter By"
              value={queryType || ''}
              onChange={(e) => {
                const value = e.target.value as QueryType | '';
                setQueryType(value || null);
              }}
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {QUERY_TYPE_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={7}>
            <TextField
              fullWidth
              size="small"
              label="Search Value"
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleApplyFilter();
                }
              }}
              disabled={!queryType}
            />
          </Grid>
          <Grid item xs={12} sm={2} md={2}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="contained"
                fullWidth
                size="small"
                onClick={handleApplyFilter}
                disabled={!queryType || !searchValue.trim()}
              >
                Apply
              </Button>
              {hasActiveFilters && (
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleClearFilter}
                >
                  Clear
                </Button>
              )}
            </Box>
          </Grid>
        </Grid>
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Workflow ID</TableCell>
              <TableCell>Step Name</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {spans.map((span: any) => {
              const linkTo = `/developer/workflows/${workflowId}/executions/${span.executionId}`;

              return (
                <TableRow 
                  key={span.executionId}
                  sx={{ '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' } }}
                >
                  <TableCell>
                    <Link 
                      to={linkTo}
                      style={{ 
                        textDecoration: 'none', 
                        color: 'inherit',
                        display: 'block',
                        width: '100%',
                        cursor: 'pointer'
                      }}
                    >
                      {span.executionId}
                    </Link>
                  </TableCell>
                  <TableCell>
                    <Link 
                      to={linkTo}
                      style={{ 
                        textDecoration: 'none', 
                        color: 'inherit',
                        display: 'block',
                        width: '100%',
                        cursor: 'pointer'
                      }}
                    >
                      {span.stepName}{span.attempt && span.attempt > 1 ? ` (Retry ${span.attempt})` : ''}
                    </Link>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <Box
        sx={{
          mt: 2,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 2,
        }}
      >
        <Typography variant="body2">
          Page {currentPageIndex + 1}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TextField
            select
            size="small"
            label="Page size"
            value={pageSize}
            onChange={(e) => handlePageSizeChange(Number(e.target.value))}
            sx={{ minWidth: 100 }}
          >
            {PAGE_SIZE_OPTIONS.map((size) => (
              <MenuItem key={size} value={size}>
                {size}
              </MenuItem>
            ))}
          </TextField>
          <Button
            variant="outlined"
            size="small"
            onClick={handlePreviousPage}
            disabled={currentPageIndex === 0}
          >
            Previous
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={handleNextPage}
            disabled={!nextPageToken}
          >
            Next
          </Button>
        </Box>
      </Box>
    </Box>
  );
}; 